import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NavBar from './nav_bar.js';
import BackButton from './BackButton.js';
import usePageSeo, { buildEventJsonLd } from './seo.js';
import useBfcacheReset from './bfcache.js';
import SupportLine from './support_line.js';
import './index.css';

// /tickets — every /tickets/<artist> page stacked into one scrollable list.
// Each poster starts a Stripe Checkout for that artist; on success Stripe
// returns the buyer to /tickets/<artist>, where the single-artist page shows
// and emails the QR code.
function AllTicketsPage() {
  const [artists, setArtists] = useState([]);
  const [priceDisplay, setPriceDisplay] = useState(null);
  const [eventName, setEventName] = useState(null);
  const [date, setDate] = useState(null);
  const [dateISO, setDateISO] = useState(null);
  const [address, setAddress] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingFor, setStartingFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/events/lineup');
        const data = await res.json();
        if (cancelled) return;
        setArtists(data.artists || []);
        setPriceDisplay(data.priceDisplay || null);
        setEventName(data.eventName || null);
        setDate(data.date || null);
        setDateISO(data.dateISO || null);
        setAddress(data.address || null);
        setConfigured(data.ticketingConfigured !== false);
      } catch {
        if (!cancelled) setError('Could not load the lineup.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Back button after the Stripe redirect restores this page frozen — clear
  // the stuck "Opening checkout…" state on the poster button.
  useBfcacheReset(() => setStartingFor(null), startingFor !== null);

  const buy = useCallback(async (slug) => {
    setStartingFor(slug);
    setError('');
    try {
      const res = await fetch(`/tickets/${encodeURIComponent(slug)}/checkout`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.msg || 'Could not start checkout.');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setStartingFor(null);
    }
  }, []);

  const jsonLd = useMemo(
    () => (eventName
      ? buildEventJsonLd({
          name: eventName,
          dateISO,
          address,
          artists,
          ticketUrl: `${window.location.origin}/tickets`,
        })
      : null),
    [eventName, dateISO, address, artists],
  );
  usePageSeo({
    title: eventName ? `${eventName} — buy tickets | MP3JUUG` : 'Tickets | MP3JUUG',
    description: eventName
      ? `Buy tickets for ${eventName}${date ? ` — ${date}` : ''}${address ? ` at ${address}` : ''}. Per-artist entry and paid song requests.`
      : 'Buy event tickets from MP3JUUG.',
    jsonLd,
  });

  return (
    <>
      <NavBar />
      <main className='page'>
        <div className='page-content'>
          <BackButton fallback='/events' />
          <h1 className='event-name'>{eventName || 'Tickets'}</h1>
          {date && <p className='event-date'>{date}</p>}
          {address && <p className='event-address'>{address}</p>}
          {priceDisplay && (
            <p className='help-text'>Each ticket {priceDisplay}. Good for one entry.</p>
          )}

          {loading ? (
            <p className='help-text'>Loading…</p>
          ) : error && artists.length === 0 ? (
            <p className='error-text'>{error}</p>
          ) : (
            <ul className='list ticket-lineup'>
              {artists.map((a) => (
                <li key={a.artist} className='card ticket-card'>
                  <h2>{a.name}</h2>
                  <button
                    type='button'
                    className='ticket-poster-btn'
                    onClick={() => buy(a.artist)}
                    disabled={startingFor !== null || configured === false}
                    aria-label={`Buy a ticket for ${a.name}`}
                  >
                    <img
                      className='ticket-poster'
                      src={a.poster}
                      alt={`${a.name} event poster`}
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallback) return;
                        e.currentTarget.dataset.fallback = '1';
                        e.currentTarget.src = '/tickets/general.svg';
                      }}
                    />
                    <span className='ticket-poster-cta'>
                      {configured === false
                        ? 'Not on sale yet'
                        : startingFor === a.artist ? 'Opening checkout…' : 'Buy ticket'}
                    </span>
                  </button>
                  <Link to={`/tickets/${a.artist}`} className='request-song-link'>
                    Request a song for {a.name} → (includes entry + 21+ shot)
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {error && artists.length > 0 && <p className='error-text'>{error}</p>}
          <SupportLine />
        </div>
      </main>
    </>
  );
}

export default AllTicketsPage;
